var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { ThemeProvider } from "@mui/material";
import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { BrowserRouter } from "react-router-dom";
import Loader from "./components/Common/Loader/Loader";
import AppRoutes from "./routes/AppRoutes";
import { acquireEntraAccessToken } from "./infrastructure/config/msalConfig";
import { exchangeEntraToken, getHttpStatus, isTokenExpired, isTokenExpiringWithin, toSessionUser, } from "./services/authService";
import { useAuthStore } from "./store/authStore";
import themeDefault, { createDynamicTheme } from "./themes/default";
import { logger } from "./utils/logger";
import SnackbarFeedback from "./components/Common/SnackbarFeedback/SnackbarFeedback";
// Una sesión de Entra se renueva cuando al JWT le quedan 5 minutos o menos (wiki §6.2).
var ENTRA_RENEWAL_THRESHOLD_SECONDS = 5 * 60;
var LoadingScreen = function () { return (_jsx("div", __assign({ style: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' } }, { children: _jsx(Loader, {}) }))); };
function App() {
    var _this = this;
    var _a = __read(useState(themeDefault), 2), theme = _a[0], setTheme = _a[1];
    useEffect(function () {
        var handleCssChange = function () {
            setTheme(createDynamicTheme());
        };
        // Observa cambios en las variables CSS
        var observer = new MutationObserver(handleCssChange);
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['style'], // Escucha cambios en los estilos (CSS variables)
        });
        return function () { return observer.disconnect(); };
    }, []);
    var _b = useAuthStore(), loading = _b.loading, setLoading = _b.setLoading, token = _b.token, logout = _b.logout, login = _b.login, authMethod = _b.authMethod;
    var timerRef = useRef(null);
    var renewingRef = useRef(false);
    // Renovación de una sesión de Entra (wiki §3.3): token silencioso de MSAL y nuevo canje.
    // Una sola renovación en curso a la vez.
    var renewEntraSession = useCallback(function () { return __awaiter(_this, void 0, void 0, function () {
        var entraAccessToken, authResponse, error_1, status_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (renewingRef.current)
                        return [2 /*return*/];
                    renewingRef.current = true;
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 5, 6, 7]);
                    return [4 /*yield*/, acquireEntraAccessToken()];
                case 2:
                    entraAccessToken = _a.sent();
                    if (!entraAccessToken) {
                        logger.info("ℹ️ MSAL no tiene cuenta para renovar la sesión, cerrando sesión...");
                        logout();
                        return [2 /*return*/];
                    }
                    return [4 /*yield*/, exchangeEntraToken(entraAccessToken)];
                case 3:
                    authResponse = _a.sent();
                    return [4 /*yield*/, login(toSessionUser(authResponse), authResponse.token, "entra")];
                case 4:
                    _a.sent();
                    return [3 /*break*/, 7];
                case 5:
                    error_1 = _a.sent();
                    status_1 = getHttpStatus(error_1);
                    if (error_1 instanceof InteractionRequiredAuthError || status_1 === 401 || status_1 === 403) {
                        logger.info("ℹ️ No se puede renovar la sesión de Microsoft, cerrando sesión...");
                        logout();
                        return [2 /*return*/];
                    }
                    // Otros fallos (p. ej. 503 o red): se reintenta en el siguiente tick hasta que caduque.
                    logger.warn("⚠️ No se ha podido renovar la sesión de Microsoft. HTTP", status_1);
                    return [3 /*break*/, 7];
                case 6:
                    renewingRef.current = false;
                    return [7 /*endfinally*/];
                case 7: return [2 /*return*/];
            }
        });
    }); }, [login, logout]);
    // Verificar token al cargar la aplicación y periódicamente
    useEffect(function () {
        // Evitar que loading quede atrapado en `true` en la primera carga
        setLoading(false);
        // Verificar si el token ha expirado al iniciar la aplicación
        if (token && isTokenExpired(token)) {
            logger.info("ℹ️ Token expirado en la carga inicial de App");
            logout();
            return;
        }
        // Configurar verificación periódica (cada 15 segundos)
        timerRef.current = window.setInterval(function () {
            if (token && isTokenExpired(token)) {
                logger.info("ℹ️ Token expirado en App, cerrando sesión...");
                logout();
                return;
            }
            // Solo las sesiones de Entra se renuevan; las de contraseña caducan como siempre.
            if (token &&
                authMethod === "entra" &&
                isTokenExpiringWithin(token, ENTRA_RENEWAL_THRESHOLD_SECONDS)) {
                renewEntraSession();
            }
        }, 15000);
        return function () {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, [setLoading, token, logout, authMethod, renewEntraSession]);
    if (loading) {
        return _jsx(LoadingScreen, {});
    }
    return (_jsx(ThemeProvider, __assign({ theme: theme }, { children: _jsx(Suspense, __assign({ fallback: _jsx(LoadingScreen, {}) }, { children: _jsxs(BrowserRouter, __assign({ future: {
                    v7_startTransition: true,
                    v7_relativeSplatPath: true
                } }, { children: [_jsx(AppRoutes, {}), _jsx(SnackbarFeedback, {})] })) })) })));
}
export default App;
